import { ArrayUtil, ModelRow, StringUtil } from "@mcleod/core";
import {
    ChangeEvent, Component, ComponentFactory, DataSourceAction, DataSourceExecutionEvent, DropdownItem,
    DynamicDbDisplayValueSearchEvent, Label, LookupModelSearchEvent, LookupModelSelectionEvent, Panel, PrintableEvent,
    Switch, TableRow, TableRowDisplayEvent, Textbox
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutFileImportProfile } from "./autogen/AutogenLayoutFileImportProfile";
import { FileImportDetail } from "./FileImportDetail";

export class FileImportProfile extends AutogenLayoutFileImportProfile {
    override onLoad() {
        this.alignSwitches();
        this.addDetailEditLoadedListener();
        this.addDoBeforeDataSourcePost();
    }

    private alignSwitches() {
        this.switchActive.captionLabelMarginBottom = 8;
        this.switchMatchOnPrevCode.captionLabelMarginBottom = 8;
    }

    private addDetailEditLoadedListener() {
        this.tableFileImportDetail.onEditLayoutLoaded = (decorator, _tableRow) => {
            const layoutDetail = decorator.layout as FileImportDetail;
            const activeRow = this.sourceFileImportProfile.activeRow;
            layoutDetail.parentDataType = activeRow.get("data_type", null);
            layoutDetail.setEffectFields = activeRow.get("set_effect_fields", []);
            layoutDetail.setEffectHelpline = activeRow.get("set_effect_helpline", null);
        };
    }

    private addDoBeforeDataSourcePost() {
        this.sourceFileImportProfile.doBeforeDataSourcePost = async () => {
            return this.checkForDuplicateDetailRecords();
        }
    }

    private checkForDuplicateDetailRecords(): boolean {
        const distinctFields = new Map<string, any>();
        this.sourceFileImportDetail.data.forEach(detailRow => {
            const tableName = detailRow.getString("table_name", "");
            const fieldName = detailRow.getString("field_name", "");
            const tableFieldName = `${tableName}.${fieldName}`;
            const fieldInfo = distinctFields.get(tableFieldName) ?? { tableName, fieldName, count: 0 };
            fieldInfo.count++;
            distinctFields.set(tableFieldName, fieldInfo);
        });

        // Set Effect fields are allowed to be duplicated since each record can have a different set effect value
        const setEffectFields = this.sourceFileImportProfile.activeRow.get("set_effect_fields", []);
        const duplicateFields = Array.from(distinctFields.values())
            .filter(fieldInfo => fieldInfo.count > 1 && !setEffectFields.includes(fieldInfo.fieldName));

        if (duplicateFields.length > 0) {
            const messageLabel = new Label({
                text: "Duplicate field mappings exist, please review the following:",
                fontBold: true,
                marginBottom: 8
            })
            const duplicatesList = duplicateFields.map(field => {
                return {
                    text: `Table: ${field.tableName}, Field: ${field.fieldName}`,
                    marginLeft: 8
                };
            });
            const fieldLabels = ComponentFactory.createBulletedList(duplicatesList);
            const errorContent = Panel.createNoMarginNoPaddingPanel({ components: [ messageLabel, ...fieldLabels ] });
            CommonDialogs.showMessage(errorContent, "Duplicate Detail Records");
            return false;
        }    
        return true;
    }

    tableFileImportDetailOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.setHelplineTooltip(tableRow);
        this.setSchemaFieldsVisible(tableRow, !StringUtil.isEmptyString(tableRow.data.get("field_name", null)));
        this.defaultQuickAddTableName(tableRow);
    }

    private setHelplineTooltip(row: TableRow) {
        const labelHelpline = row.findComponentById("labelHelpline") as Label;
        labelHelpline.tooltip = labelHelpline.text;
    }

    private defaultQuickAddTableName(row: TableRow) {
        if (row?.isQuickAddRow()) {
            // Doing this in a setTimeout is not ideal, but it gets around the issue of the display of the textbox's
            // data being async (which without the timeout, will often result in the textbox being cleared by the
            // display of the quick add row's default data, which does not include a value for the table name).
            setTimeout(() => {
                const textboxTableName = row.findComponentById("textboxTableName") as Textbox;
                const textboxItems = textboxTableName.items as [];
                if (textboxTableName.isEmpty() && ArrayUtil.getLength(textboxItems) === 1) {
                    const firstItem = ArrayUtil.getFirstElement(textboxItems);
                    if (firstItem instanceof DropdownItem) {
                        textboxTableName.selectedItem = firstItem;
                    }
                }
            }, 750);
        }
    }

    switchUseDefaultOnPrintable(event: PrintableEvent) {
        this.setPrintableLabelText(event);
    }

    switchAllowUpdateOnPrintable(event: PrintableEvent) {
        this.setPrintableLabelText(event);
    }

    switchTruncateValueOnPrintable(event: PrintableEvent) {
        this.setPrintableLabelText(event);
    }

    private setPrintableLabelText(event: PrintableEvent) {
        if (event.printableLabel != null) {
            const component = event.target as Switch;
            event.printableLabel.text = component.checked ? "Yes" : "No";
        }
    }

    textboxTableNameBeforeDynamicDbDisplayValueSearch(event: DynamicDbDisplayValueSearchEvent) {
        this.addDataTypeToEvent(event);
    }

    textboxTableNameOnChange(event: ChangeEvent) {
        const tableNameTextbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(tableNameTextbox);
        const fieldNameFields = tableRow.findComponentsByField("field_name");
        if (event.userInitiatedChange) {
            fieldNameFields?.forEach(field => {
                if (field instanceof Textbox) {
                    field.clear();
                }
            });
        }
        fieldNameFields?.forEach(field => field.enabled = !tableNameTextbox.isEmpty());
    }

    textboxFieldNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.data_type = this.textboxDataType
            .getFirstLookupModelData()?.get(this.textboxDataType.lookupModelResultField);
        const fieldNameTextbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(fieldNameTextbox);
        const tableNameFields = tableRow.findComponentsByField("table_name");
        tableNameFields?.forEach(field => event.filter.selected_table_name = (field as Textbox).selectedItem?.value);
    }

    textboxFieldNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.updateSchemaFieldInfo(event);
    }

    private updateSchemaFieldInfo(event: LookupModelSelectionEvent) {
        let schemaFieldsVisible = false;
        let caption = "";
        let helpline = "";
        let required = "";
        let format = "";
        let upshifted = "";
        if (event.hasSelection()) {
            schemaFieldsVisible = true;
            const fieldInfo = event.selection;
            caption = fieldInfo.getString("caption", "");
            format = fieldInfo.getString("format", "");
            helpline = fieldInfo.getString("helpline", "");
            required = fieldInfo.getString("required", "");
            upshifted = fieldInfo.getString("upshifted", "");
        }
        const tableRow = TableRow.getContainingTableRow(event.target);
        const data = tableRow.data;
        data.set("caption", caption);
        data.set("format", format);
        data.set("helpline", helpline);
        data.set("required", required);
        data.set("upshifted", upshifted);

        // Have to call displayData() because the table's quick add row doesn't get monitored by the DataSource's
        // field update listener
        const rowIndex = this.sourceFileImportDetail.findRowInData(data);
        this.forEveryTableRowChildComponent(tableRow, component => component.displayData(data, this.sourceFileImportDetail.data, rowIndex));
        this.setSchemaFieldsVisible(tableRow, schemaFieldsVisible);
    }

    private setSchemaFieldsVisible(tableRow: TableRow, value: boolean) {
        this.forEveryTableRowChildComponent(tableRow, component => component.visible = value);
    }

    private forEveryTableRowChildComponent(tableRow: TableRow, callback: (component: Component) => void) {
        const compFields = [ "caption", "format", "helpline", "required", "upshifted" ];
        tableRow.forEveryChildComponent(component => {
        if (compFields.includes(component.field))
            callback(component);
        });
    }

    textboxDataTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedDataType = event.selection;
        this.resetActionTypeDropdownItems();
        this.textboxActionType.enabled = !this.textboxDataType.isEmpty();
        this.evaluateMatchOnPreviousCodeAvailability(selectedDataType);
        this.tableFileImportDetail.resetQuickAddRow();
    }

    private resetActionTypeDropdownItems() {
        this.sourceFileImportProfile.activeRow.set("action_type", null);
        this.textboxActionType.refreshDynamicItems();
    }

    private evaluateMatchOnPreviousCodeAvailability(selectedDataType: ModelRow) {
        this.switchMatchOnPrevCode.visible = selectedDataType?.getBoolean("match_on_prev_code_allowed", false) ?? false;
    }

    sourceFileImportProfileAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setMatchOnPreviousIdVisbility();
            this.refreshQuickAddRowTableNameValues();
        }
    }

    private setMatchOnPreviousIdVisbility() {
        const matchOnPrevCodeAllowed =
                this.sourceFileImportProfile.activeRow?.getBoolean("match_on_prev_code_allowed", false) ?? false;
            this.switchMatchOnPrevCode.visible = matchOnPrevCodeAllowed;
    }

    // Due to the timing of when the data type field populates, and when the table's quick add row is created, the data
    // type may or may not be present when the quick add row is created.  Thus the purpose of this method is to force
    // an update of the quick add row's table name textbox's DB/display values.
    private refreshQuickAddRowTableNameValues() {
        if (!this.textboxDataType.isEmpty()) {
            const quickAddRow = this.tableFileImportDetail?.quickAddRow;
            if (quickAddRow != null) {
                const quickAddRowTableNameTextbox =
                    ArrayUtil.getFirstElement(quickAddRow.findComponentsByField("table_name")) as Textbox;
                quickAddRowTableNameTextbox.refreshDynamicItems(quickAddRow.data, [ quickAddRow.data ], 0);
            }
        }
    }

    textboxActionTypeBeforeDynamicDbDisplayValueSearch(event: DynamicDbDisplayValueSearchEvent) {
        this.addDataTypeToEvent(event);
    }

    private addDataTypeToEvent(event: DynamicDbDisplayValueSearchEvent) {
        const dataType = this.sourceFileImportProfile.activeRow?.get("data_type");
        if (!StringUtil.isEmptyString(dataType)) {
            event.addAddlValue("data_type", dataType);
        }
    }

    switchRequiredOnPrintable(event: PrintableEvent) {
        this.keepSwitchPrintable(event);
    }

    switchUpshiftedOnPrintable(event: PrintableEvent) {
        this.keepSwitchPrintable(event);
    }

    private keepSwitchPrintable(event: PrintableEvent) {
        (event.target as Switch).printable = true;
    }
}
